import React, {
    useEffect,
    useState,
    useContext,
    useCallback,
    useRef,
} from 'react';
import { Fief } from '@fief/fief';
import { useLocation } from 'react-router-dom';
import {
    useFiefAuth,
    useFiefUserinfo,
    useFiefTokenInfo,
} from '@fief/fief/react';
import { GlobalContext } from 'GlobalContext';
import { convertLegacyProps } from 'antd/es/button';
import { useNavigate } from 'react-router-dom';
import { useLogoutHelper } from 'utils/util';
export function SessionCheck() {
    const { logout } = useLogoutHelper();
    const fiefAuth = useFiefAuth();
    const navigate = useNavigate();

    const {
        setAccessToken,
        accessToken,
        accessTokenExpiredTime,
        setAccessTokenExpiredTime,
        setRefreshSession,
    } = useContext(GlobalContext);
    const [showLogoutWarning, setShowLogoutWarning] = useState(false);
    // console.log('accessToken', accessToken);
    const location = useLocation();
    const tokenInfo = useFiefTokenInfo();
    // session refresh buffer (s)
    // don't refresh tokens if tokens refreshed in the last sessionRefreshBuffer seconds
    const sessionRefreshBuffer = 60; // seconds
    // session duration (min)
    const sessionDuration = 20; // minutes
    // session hint time (min)
    const sessionHintTime = 5; // minutes
    const sessionStartTimeRef = useRef(Date.now());
    const sessionEndTimeRef = useRef(Date.now() + sessionDuration * 60 * 1000);
    const [hasMounted, setHasMounted] = useState(false);

    useEffect(() => {
        setHasMounted(true);
    }, []);

    // useEffect(() => {
    //     console.log('tokenInfo', tokenInfo);
    // }, [tokenInfo]);

    const [secondsLeft, setSecondsLeft] = useState(sessionDuration * 60);

    // console.log('tokenInfo', tokenInfo);
    const fief = new Fief({
        baseURL: process.env.REACT_APP_FIEF_BASE_URL,
        clientId: process.env.REACT_APP_FIEF_CLIENT_ID,
    });

    function secondsLeftToText() {
        const minutes = Math.floor(secondsLeft / 60);
        const seconds = Math.floor(secondsLeft % 60);
        const minutesText =
            minutes > 0 ? `${minutes} minute${minutes > 1 ? 's' : ''}` : '';
        const secondsText =
            seconds > 0 ? `${seconds} second${seconds > 1 ? 's' : ''}` : '';
        return `${minutesText}${
            minutes > 0 && seconds > 0 ? ' and ' : ''
        }${secondsText}`;
    }

    useEffect(() => {
        const checkSession = () => {
            if (accessTokenExpiredTime !== 0) {
                const sessionEndTime = Math.floor(
                    Math.min(accessTokenExpiredTime, sessionEndTimeRef.current)
                );

                const secondsLeft = (sessionEndTime - Date.now()) / 1000;
                setSecondsLeft(secondsLeft);
                if (secondsLeft <= 0) {
                    logout();
                }

                setShowLogoutWarning(secondsLeft <= sessionHintTime * 60);
            }
        };

        const intervalId = setInterval(checkSession, 1000);
        // Listen for window/tab focus or visibility changes
        window.addEventListener('visibilitychange', checkSession);
        window.addEventListener('focus', checkSession);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('visibilitychange', checkSession);
            window.removeEventListener('focus', checkSession);
        };
    }, [accessTokenExpiredTime]);

    const logoutRefreshTokenError = useCallback(() => {
        fiefAuth.logout(`${window.location.protocol}//${window.location.host}`);
    }, [fiefAuth]);

    async function refreshTokens(location = '') {
        // console.log((Date.now() - sessionStartTimeRef.current) / 1000);
        if (
            sessionStartTimeRef.current > 0 &&
            Date.now() - sessionStartTimeRef.current <
                sessionRefreshBuffer * 1000
        ) {
            return;
        }
        sessionStartTimeRef.current = Date.now();
        sessionEndTimeRef.current = Date.now() + sessionDuration * 60 * 1000;
        // console.log(
        //     'before guard',
        //     'accessTokenExpiredTime',
        //     accessTokenExpiredTime,
        //     'sessionEndTimeRef',
        //     sessionEndTimeRef.current,
        //     accessTokenExpiredTime - sessionEndTimeRef.current
        // );
        if (accessTokenExpiredTime > sessionEndTimeRef.current) {
            // console.log(
            //     'accessTokenExpiredTime',
            //     accessTokenExpiredTime,
            //     'sessionEndTimeRef',
            //     sessionEndTimeRef
            // );
            console.log('session renewed without refreshing token');
            return;
        }
        const authstate = JSON.parse(sessionStorage.getItem('fief-authstate'));
        const refreshToken = authstate.tokenInfo.refresh_token;
        const baseUrl = `${window.location.protocol}//${window.location.host}`;
        const fullBaseUrl = `${window.location.origin}${window.location.pathname}`;

        const config = await fief.getOpenIDConfiguration();
        try {
            if (refreshToken) {
                // console.log(
                //     'await fief.authRefreshToken(refreshToken);',
                //     refreshToken
                // );
                const refreshed = await fief.authRefreshToken(refreshToken);
                sessionStorage.setItem(
                    'fief-authstate',
                    JSON.stringify({
                        tokenInfo: refreshed[0],
                        userinfo: refreshed[1],
                        issuedAt: Date.now(),
                        expiresAt: Date.now() + refreshed[0].expires_in * 1000,
                    })
                );
                setAccessToken(refreshed[0].access_token);
                setAccessTokenExpiredTime(
                    Date.now() + (refreshed[0].expires_in - 60) * 1000
                );
                // console.log(
                //     'setAccessTokenExpiredTime',
                //     (refreshed[0].expires_in - 60) * 1000,
                //     Date.now() + (refreshed[0].expires_in - 60) * 1000
                // );
                // console.log(
                //     'sessionEndTimeRef',
                //     sessionDuration * 60 * 1000,
                //     Date.now() + (refreshed[0].expires_in - 60) * 1000
                // );
            } else {
                console.error('No refresh token found');
                logout();
            }
        } catch (error) {
            console.error('Error refreshing token:', error);
            logoutRefreshTokenError();
        }
    }
    const [prevLocation, setPrevLocation] = useState('');

    function refreshSession() {
        refreshTokens();
    }

    useEffect(() => {
        if (!hasMounted || tokenInfo === null) return;
        // Set the refreshSession function into context
        setRefreshSession(() => refreshSession);
        // console.log('useEffect [setRefreshSession]');
    }, [setRefreshSession, tokenInfo, hasMounted]);

    useEffect(() => {
        if (!hasMounted || tokenInfo === null) return;
        // console.log('useEffect [location]', accessToken);
        if (accessToken === '' || prevLocation === location) return;
        setPrevLocation(location);
        // console.log('useEffect [location, accessToken]');
        refreshTokens(location);
        // You can add any side effects here (fetching data, analytics, etc.)
    }, [location, accessToken, tokenInfo, hasMounted]);
    return (
        <>
            {showLogoutWarning && (
                <div
                    style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: '#fff3cd',
                        color: '#856404',
                        padding: '24px 36px',
                        borderRadius: '12px',
                        fontSize: '1.2em',
                        fontWeight: 500,
                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                        textAlign: 'center',
                        zIndex: 9999,
                        minWidth: '320px',
                    }}
                >
                    Logging out in <strong>{secondsLeftToText()}</strong> due to
                    inactivity.
                    <br />
                    <div style={{ marginTop: '16px' }}>
                        <button
                            onClick={refreshTokens}
                            style={{
                                marginRight: '12px',
                                padding: '8px 16px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                            }}
                        >
                            Remain Logged In
                        </button>
                        <button
                            onClick={logout}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                            }}
                        >
                            Log Out
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
